import { Button } from "@/components/ui/button"
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs"
import { Toggle } from "@/components/ui/toggle"
import { Switch } from "@/components/ui/switch"
import { Badge } from "@/components/ui/badge"
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert"
import { LoadingSpinner } from "@/components/ui/LoadingSpinner"
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group"
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from "@/components/ui/carousel"
import { Progress } from "@/components/ui/progress"
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@/components/ui/collapsible"
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion"
import { AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog"
import { AspectRatio } from "@/components/ui/aspect-ratio"
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar"
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb"
import { Checkbox } from "@/components/ui/checkbox"
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu"
import { Drawer, DrawerTrigger, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription, DrawerFooter } from "@/components/ui/drawer"
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card"
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp"
import { Car, Menu, Home, Plus, BarChart3, Settings } from "lucide-react"
import { Label } from "@/components/ui/label"
import { LoadingScreen } from "@/components/ui/loading-screen"
import { Menubar, MenubarMenu, MenubarTrigger, MenubarContent, MenubarItem } from "@/components/ui/menubar"
import { NavigationMenu, NavigationMenuList, NavigationMenuItem, NavigationMenuTrigger, NavigationMenuContent, NavigationMenuLink } from "@/components/ui/navigation-menu"
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from "@/components/ui/pagination"
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from "@/components/ui/resizable"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"
import { Sheet, SheetTrigger, SheetContent } from "@/components/ui/sheet"
import { Skeleton } from "@/components/ui/skeleton"
import { Slider } from "@/components/ui/slider"
import TutorialSlideDeck from '@/components/tutorial/TutorialSlideDeck';
import TutorialProgress from '@/components/tutorial/TutorialProgress';
import TutorialNavigation from '@/components/tutorial/TutorialNavigation';
import { tutorialSlides } from '@/components/tutorial/tutorialData';
import MechanicTag from '@/components/mechanic-share/MechanicTag';
import ShareLinkSection from '@/components/mechanic-share/ShareLinkSection';
import {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { Toaster } from "@/components/ui/toaster";
import { Calendar } from "@/components/ui/calendar";
import { CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Link } from "react-router-dom";
import HomeIcon from '@/components/icons/HomeIcon';
import HistoryIcon from '@/components/icons/HistoryIcon';
import UserIcon from '@/components/icons/UserIcon';

// Toast demo component
function ToastDemo() {
  const { toast } = useToast();

  return (
    <div className="space-y-2">
      <Button
        variant="secondary"
        className="border"
        onClick={() => {
          toast({
            title: "Default Toast",
            description: "This is a default toast message",
          });
        }}
      >
        Show Toast
      </Button>
      
      <Button
        variant="secondary"
        className="border"
        onClick={() => {
          toast({
            variant: "destructive",
            title: "Error Toast",
            description: "This is an error toast message",
          });
        }}
      >
        Show Error Toast
      </Button>
    </div>
  );
}

export function ComponentPlayground() {
  return (
    <div className="container py-10 space-y-20">
      <h1 className="text-3xl font-bold mb-8">Component Playground</h1>

      <Card className="p-6">
        <h2 className="text-xl font-bold mb-4">Buttons</h2>
        
        <div className="space-y-8">
          {/* Regular width buttons */}
          <div>
            <h3 className="text-sm text-muted-foreground mb-4">Regular (147px)</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <h4 className="text-sm text-muted-foreground">Default</h4>
                <Button>Primary</Button>
                <Button variant="secondary">Secondary</Button>
                <Button variant="destructive">Destructive</Button>
                <Button variant="link">Link</Button>
              </div>
              
              <div className="space-y-4">
                <h4 className="text-sm text-muted-foreground">Disabled</h4>
                <Button disabled>Primary</Button>
                <Button variant="secondary" disabled>Secondary</Button>
                <Button variant="destructive" disabled>Destructive</Button>
                <Button variant="link" disabled>Link</Button>
              </div>
            </div>
          </div>

          {/* Wide buttons */}
          <div>
            <h3 className="text-sm text-muted-foreground mb-4">Wide (315px)</h3>
            <div className="space-y-4">
              <div className="space-y-4">
                <h4 className="text-sm text-muted-foreground">Default</h4>
                <Button size="wide">Primary</Button>
                <Button variant="secondary" size="wide">Secondary</Button>
                <Button variant="destructive" size="wide">Destructive</Button>
                <Button variant="link" size="wide">Link</Button>
              </div>
              
              <div className="space-y-4">
                <h4 className="text-sm text-muted-foreground">Disabled</h4>
                <Button size="wide" disabled>Primary</Button>
                <Button variant="secondary" size="wide" disabled>Secondary</Button>
                <Button variant="destructive" size="wide" disabled>Destructive</Button>
                <Button variant="link" size="wide" disabled>Link</Button>
              </div>
            </div>
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <h2 className="text-xl font-bold mb-4">Inputs</h2>
        
        <div className="space-y-8">
          {/* Standard height inputs */}
          <div>
            <h3 className="text-sm text-muted-foreground mb-4">Standard Height</h3>
            <div className="space-y-4 max-w-sm">
              <Input placeholder="Default input" />
              <Input placeholder="Disabled input" disabled />
              <Input placeholder="Error input" error />
            </div>
          </div>

          {/* High inputs */}
          <div>
            <h3 className="text-sm text-muted-foreground mb-4">High Input</h3>
            <div className="space-y-4 max-w-sm">
              <Input className="h-12" placeholder="Default high input" />
              <Input className="h-12" placeholder="Disabled high input" disabled />
              <Input className="h-12" placeholder="Error high input" error />
            </div>
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <h2 className="text-xl font-bold mb-4">Tabs</h2>
        <Tabs defaultValue="account">
          <TabsList>
            <TabsTrigger value="account">Account</TabsTrigger>
            <TabsTrigger value="password">Password</TabsTrigger>
            <TabsTrigger value="settings">Settings</TabsTrigger>
          </TabsList>
          <TabsContent value="account">
            <p className="text-sm text-muted-foreground">Account settings and preferences</p>
          </TabsContent>
          <TabsContent value="password">
            <p className="text-sm text-muted-foreground">Change your password here</p>
          </TabsContent>
          <TabsContent value="settings">
            <p className="text-sm text-muted-foreground">Other settings</p>
          </TabsContent>
        </Tabs>
      </Card>

      <Card className="p-6">
        <h2 className="text-xl font-bold mb-4">Badges & Alerts</h2>
        <div className="space-y-4">
          <div className="space-x-2">
            <Badge>Default</Badge>
            <Badge variant="secondary">Secondary</Badge>
            <Badge variant="destructive">Destructive</Badge>
            <Badge className="border">Outline</Badge>
          </div>

          {/* Records Status Badges */}
          <div className="space-y-2">
            <h4 className="text-sm text-muted-foreground">Records Status Badges</h4>
            <div className="flex flex-wrap gap-2 items-center">
              <Badge
                className="px-2 py-0.5 h-5 text-[10px] font-medium border-none"
                style={{ backgroundColor: '#CBCBCB', color: '#070707' }}
              >
                Draft
              </Badge>
              <Badge
                className="px-2 py-0.5 h-5 text-[10px] font-medium border-none"
                style={{ backgroundColor: '#e7fe52', color: '#070707' }}
              >
                Open
              </Badge>
              <Badge
                className="px-2 py-0.5 h-5 text-[10px] font-medium border-none"
                style={{ backgroundColor: '#bfd0fd', color: '#070707' }}
              >
                Recurred
              </Badge>
              <Badge
                className="px-2 py-0.5 h-5 text-[10px] font-medium border-none"
                style={{ backgroundColor: '#a3f88f', color: '#070707' }}
              >
                Resolved
              </Badge>
            </div>
          </div>
          
          <div className="space-y-4">
            <Alert>
              <AlertTitle>Default Alert</AlertTitle>
              <AlertDescription>
                This is a default alert with title and description.
              </AlertDescription>
            </Alert>
            
            <Alert variant="destructive">
              <AlertTitle>Error Alert</AlertTitle>
              <AlertDescription>
                This is a destructive alert for error states.
              </AlertDescription>
            </Alert>
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <h2 className="text-xl font-bold mb-4">Toggles & Switches</h2>
        <div className="space-y-8">
          {/* Single Toggles */}
          <div>
            <h3 className="text-sm text-muted-foreground mb-4">Toggles (Button Style)</h3>
            <div className="flex flex-wrap gap-4">
              <Toggle>Unpressed Toggle</Toggle>
              <Toggle pressed>Pressed Toggle</Toggle>
              <Toggle disabled>Disabled Toggle</Toggle>
              <Toggle disabled pressed>Disabled + Pressed</Toggle>
            </div>
          </div>

          {/* Toggle Groups */}
          <div>
            <h3 className="text-sm text-muted-foreground mb-4">Toggle Groups</h3>
            <div className="space-y-4">
              <ToggleGroup type="single" defaultValue="center">
                <ToggleGroupItem value="left">Left</ToggleGroupItem>
                <ToggleGroupItem value="center">Center</ToggleGroupItem>
                <ToggleGroupItem value="right">Right</ToggleGroupItem>
              </ToggleGroup>

              <ToggleGroup type="multiple">
                <ToggleGroupItem value="bold">Bold</ToggleGroupItem>
                <ToggleGroupItem value="italic">Italic</ToggleGroupItem>
                <ToggleGroupItem value="underline">Underline</ToggleGroupItem>
              </ToggleGroup>
            </div>
          </div>

          {/* Switches */}
          <div>
            <h3 className="text-sm text-muted-foreground mb-4">Switches (Toggle Style)</h3>
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex items-center gap-2">
                <Switch id="airplane-mode" />
                <label htmlFor="airplane-mode">Airplane Mode</label>
              </div>
              <div className="flex items-center gap-2">
                <Switch id="checked" defaultChecked />
                <label htmlFor="checked">Checked</label>
              </div>
              <div className="flex items-center gap-2">
                <Switch id="disabled" disabled />
                <label htmlFor="disabled">Disabled</label>
              </div>
              <div className="flex items-center gap-2">
                <Switch id="disabled-checked" disabled defaultChecked />
                <label htmlFor="disabled-checked">Disabled Checked</label>
              </div>
            </div>
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <h2 className="text-xl font-bold mb-4">Loading States</h2>
        <div className="space-y-4">
          <div className="space-x-4">
            <LoadingSpinner />
            <LoadingSpinner className="h-8 w-8" />
          </div>
          <div className="space-x-4">
            <Button disabled>
              <LoadingSpinner className="mr-2 h-4 w-4" />
              Loading...
            </Button>
            <Button variant="secondary" disabled>
              <LoadingSpinner className="mr-2 h-4 w-4" />
              Processing
            </Button>
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <h2 className="text-xl font-bold mb-4">Cards</h2>
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Card Title</CardTitle>
              <CardDescription>Card Description</CardDescription>
            </CardHeader>
            <CardContent>
              <p>Card Content</p>
            </CardContent>
            <CardFooter>
              <p>Card Footer</p>
            </CardFooter>
          </Card>

          {/* Example of CarCard */}
          <Card className="p-4">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="text-lg font-semibold">My Car</h3>
                <p className="text-sm text-gray-500">Toyota Camry</p>
                <p className="text-sm text-gray-500">2022</p>
                <p className="text-sm text-gray-500">15,000 km</p>
              </div>
              <Car className="text-gray-400" size={32} />
            </div>
          </Card>
        </div>
      </Card>

      <Card className="p-6">
        <h2 className="text-xl font-bold mb-4">Carousel</h2>
        <Carousel className="w-full max-w-sm">
          <CarouselContent>
            {Array.from({ length: 5 }).map((_, index) => (
              <CarouselItem key={index}>
                <Card>
                  <CardContent className="flex aspect-square items-center justify-center p-6">
                    <span className="text-4xl font-semibold">{index + 1}</span>
                  </CardContent>
                </Card>
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious />
          <CarouselNext />
        </Carousel>
      </Card>

      <Card className="p-6">
        <h2 className="text-xl font-bold mb-4">Progress</h2>
        <div className="space-y-4">
          <Progress value={33} />
          <Progress value={66} />
          <Progress value={100} />
        </div>
      </Card>

      <Card className="p-6">
        <h2 className="text-xl font-bold mb-4">Collapsible</h2>
        <Collapsible className="space-y-2">
          <CollapsibleTrigger className="flex w-full items-center justify-between p-4 bg-gray-100 rounded-md">
            <h4 className="text-sm font-semibold">Click to expand</h4>
          </CollapsibleTrigger>
          <CollapsibleContent className="p-4">
            <p className="text-sm text-gray-500">
              This is the collapsible content that can be toggled.
            </p>
          </CollapsibleContent>
        </Collapsible>
      </Card>

      <Card className="p-6">
        <h2 className="text-xl font-bold mb-4">Car Components</h2>
        <div className="space-y-4">
          {/* Car Carousel Example */}
          <Carousel
            opts={{ 
              align: 'start',
              loop: true
            }}
            className="w-full"
          >
            <CarouselContent>
              {Array.from({ length: 3 }).map((_, index) => (
                <CarouselItem key={index} className="basis-full md:basis-1/2 lg:basis-1/3">
                  <Card className="p-4">
                    <div className="flex items-center space-x-3">
                      <Car className="h-10 w-10 text-gray-400" />
                      <div>
                        <h3 className="text-base font-medium">Car {index + 1}</h3>
                        <p className="text-sm text-gray-500">Toyota Camry</p>
                      </div>
                    </div>
                  </Card>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="left-1 md:-left-5" />
            <CarouselNext className="right-1 md:-right-5" />
          </Carousel>
        </div>
      </Card>

      <Card className="p-6">
        <h2 className="text-xl font-bold mb-4">Accordion</h2>
        <Accordion type="single" collapsible>
          <AccordionItem value="item-1">
            <AccordionTrigger>Is it accessible?</AccordionTrigger>
            <AccordionContent>
              Yes. It adheres to the WAI-ARIA design pattern.
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="item-2">
            <AccordionTrigger>Is it styled?</AccordionTrigger>
            <AccordionContent>
              Yes. It comes with default styles that matches your theme.
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </Card>

      <Card className="p-6">
        <h2 className="text-xl font-bold mb-4">Alert Dialog</h2>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="destructive">Delete Account</Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
              <AlertDialogDescription>
                This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction>Continue</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </Card>

      <Card className="p-6">
        <h2 className="text-xl font-bold mb-4">Avatar</h2>
        <div className="flex gap-4">
          <Avatar>
            <AvatarImage src="https://github.com/shadcn.png" alt="@shadcn" />
            <AvatarFallback>CN</AvatarFallback>
          </Avatar>
          <Avatar>
            <AvatarFallback>JD</AvatarFallback>
          </Avatar>
        </div>
      </Card>

      <Card className="p-6">
        <h2 className="text-xl font-bold mb-4">Breadcrumb</h2>
        <Breadcrumb>
          <BreadcrumbItem>
            <BreadcrumbLink href="/">Home</BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink href="/components">Components</BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>Breadcrumb</BreadcrumbPage>
          </BreadcrumbItem>
        </Breadcrumb>
      </Card>

      <Card className="p-6">
        <h2 className="text-xl font-bold mb-4">Checkbox & Dropdown</h2>
        <div className="space-y-4">
          <div className="flex items-center space-x-2">
            <Checkbox id="terms" />
            <label htmlFor="terms">Accept terms and conditions</label>
          </div>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="secondary">
                <Menu className="mr-2" />
                Menu
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem>Profile</DropdownMenuItem>
              <DropdownMenuItem>Settings</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem>Logout</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </Card>

      <Card className="p-6">
        <h2 className="text-xl font-bold mb-4">Hover Card</h2>
        <HoverCard>
          <HoverCardTrigger asChild>
            <Button variant="link">@username</Button>
          </HoverCardTrigger>
          <HoverCardContent>
            <div className="flex justify-between space-x-4">
              <Avatar>
                <AvatarImage src="https://github.com/vercel.png" />
                <AvatarFallback>VC</AvatarFallback>
              </Avatar>
              <div className="space-y-1">
                <h4 className="text-sm font-semibold">@username</h4>
                <p className="text-sm">The creator of this component.</p>
              </div>
            </div>
          </HoverCardContent>
        </HoverCard>
      </Card>

      <Card className="p-6">
        <h2 className="text-xl font-bold mb-4">OTP Input</h2>
        <InputOTP maxLength={6}>
          <InputOTPGroup>
            <InputOTPSlot index={0} />
            <InputOTPSlot index={1} />
            <InputOTPSlot index={2} />
            <InputOTPSlot index={3} />
            <InputOTPSlot index={4} />
            <InputOTPSlot index={5} />
          </InputOTPGroup>
        </InputOTP>
      </Card>

      <Card className="p-6">
        <h2 className="text-xl font-bold mb-4">Form Controls</h2>
        <div className="space-y-6">
          <div className="space-y-2">
            <Label>Radio Group</Label>
            <RadioGroup defaultValue="option-1">
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="option-1" id="option-1" />
                <Label htmlFor="option-1">Option 1</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="option-2" id="option-2" />
                <Label htmlFor="option-2">Option 2</Label>
              </div>
            </RadioGroup>
          </div>

          <div className="space-y-2">
            <Label>Select</Label>
            <Select>
              <SelectTrigger>
                <SelectValue placeholder="Select an option" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">Option 1</SelectItem>
                <SelectItem value="2">Option 2</SelectItem>
                <SelectItem value="3">Option 3</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Slider</Label>
            <Slider defaultValue={[50]} max={100} step={1} />
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <h2 className="text-xl font-bold mb-4">Navigation</h2>
        <div className="space-y-6">
          <Menubar>
            <MenubarMenu>
              <MenubarTrigger>File</MenubarTrigger>
              <MenubarContent>
                <MenubarItem>New</MenubarItem>
                <MenubarItem>Open</MenubarItem>
                <MenubarItem>Save</MenubarItem>
              </MenubarContent>
            </MenubarMenu>
          </Menubar>

          <NavigationMenu>
            <NavigationMenuList>
              <NavigationMenuItem>
                <NavigationMenuTrigger>Item One</NavigationMenuTrigger>
                <NavigationMenuContent>
                  <NavigationMenuLink>Link</NavigationMenuLink>
                </NavigationMenuContent>
              </NavigationMenuItem>
            </NavigationMenuList>
          </NavigationMenu>

          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious href="#" />
              </PaginationItem>
              <PaginationItem>
                <PaginationLink href="#">1</PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationNext href="#" />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      </Card>

      <Card className="p-6">
        <h2 className="text-xl font-bold mb-4">Overlays</h2>
        <div className="flex gap-4">
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="secondary">Open Popover</Button>
            </PopoverTrigger>
            <PopoverContent>
              <p>Popover content</p>
            </PopoverContent>
          </Popover>

          <Sheet>
            <SheetTrigger asChild>
              <Button variant="secondary">Open Sheet</Button>
            </SheetTrigger>
            <SheetContent>
              <h3 className="text-lg font-bold">Sheet Title</h3>
              <p>Sheet content</p>
            </SheetContent>
          </Sheet>
        </div>
      </Card>

      <Card className="p-6">
        <h2 className="text-xl font-bold mb-4">Layout Components</h2>
        <div className="space-y-6">
          <ResizablePanelGroup direction="horizontal" className="h-[200px] max-w-md rounded-lg border">
            <ResizablePanel>
              <div className="flex h-full items-center justify-center p-6">
                <span className="font-semibold">Panel 1</span>
              </div>
            </ResizablePanel>
            <ResizableHandle />
            <ResizablePanel>
              <div className="flex h-full items-center justify-center p-6">
                <span className="font-semibold">Panel 2</span>
              </div>
            </ResizablePanel>
          </ResizablePanelGroup>

          <ScrollArea className="h-[200px] w-full rounded-md border p-4">
            <div className="space-y-4">
              {Array.from({ length: 20 }).map((_, i) => (
                <div key={i} className="text-sm">Scrollable content {i + 1}</div>
              ))}
            </div>
          </ScrollArea>
        </div>
      </Card>

      <Card className="p-6">
        <h2 className="text-xl font-bold mb-4">Loading States</h2>
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <Skeleton className="h-12 w-12 rounded-full" />
            <div className="space-y-2">
              <Skeleton className="h-4 w-[250px]" />
              <Skeleton className="h-4 w-[200px]" />
            </div>
          </div>

          <div className="border rounded-lg p-4">
            <LoadingScreen message="Loading component..." />
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <h2 className="text-xl font-bold mb-4">Dividers</h2>
        <div className="space-y-4">
          <div className="flex items-center space-x-4">
            <span>Item 1</span>
            <Separator orientation="vertical" className="h-4" />
            <span>Item 2</span>
            <Separator orientation="vertical" className="h-4" />
            <span>Item 3</span>
          </div>
          <Separator className="my-4" />
          <div className="space-y-1">
            <h4 className="text-sm font-medium">Horizontal separator above</h4>
          </div>
        </div>
      </Card>

      {/* Tutorial Components */}
      <div className="space-y-8">
        <h2 className="text-2xl font-bold">Tutorial Components</h2>
        
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Tutorial Slide Deck</h3>
          <div className="border rounded-lg p-6 bg-background">
            <TutorialSlideDeck 
              slides={tutorialSlides}
              onSlideChange={(index) => console.log('Slide changed:', index)}
              onApiReady={(api) => console.log('Carousel API ready')}
            />
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Tutorial Progress</h3>
          <div className="border rounded-lg p-6 bg-background">
            <TutorialProgress currentSlide={1} totalSlides={4} />
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Tutorial Navigation</h3>
          <div className="border rounded-lg p-6 bg-background">
            <TutorialNavigation 
              currentSlide={1}
              totalSlides={4}
              onNext={() => console.log('Next clicked')}
              onSkip={() => console.log('Skip clicked')}
              isCompleting={false}
            />
          </div>
        </div>
      </div>

      {/* Mechanic Share Components */}
      <div className="space-y-8">
        <h2 className="text-2xl font-bold">Mechanic Share Components</h2>
        
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Mechanic Tag</h3>
          <div className="border rounded-lg p-6 bg-background">
            <MechanicTag 
              repair={{
                id: 'demo-repair-1',
                title: 'Oil change',
                date: new Date().toISOString(),
                addedByMechanic: true,
                mechanicName: "John Smith",
                garageName: "ABC Auto Repair",
                mechanicAddedDate: new Date().toISOString()
              } as any}
            />
            <MechanicTag 
              notes="Added by Mike Johnson from XYZ Garage via mechanic link on Jan 15, 2024"
            />
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Share Link Section</h3>
          <div className="border rounded-lg p-6 bg-background">
            <ShareLinkSection 
              car={{
                id: "demo-car",
                brand: "Toyota",
                model: "Camry",
                year: 2020,
                nickname: "Daily Driver",
                licensePlate: "ABC123"
              } as any}
            />
          </div>
        </div>
      </div>

      {/* Data Display Components */}
      <div className="space-y-8">
        <h2 className="text-2xl font-bold">Data Display Components</h2>
        
        {/* Table */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Table</h3>
          <div className="border rounded-lg p-6 bg-background">
            <Table>
              <TableCaption>A demo table</TableCaption>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow>
                  <TableCell>John Doe</TableCell>
                  <TableCell>Active</TableCell>
                  <TableCell>2024-01-01</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Jane Smith</TableCell>
                  <TableCell>Pending</TableCell>
                  <TableCell>2024-01-02</TableCell>
                </TableRow>
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TableCell colSpan={3}>Total: 2 entries</TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          </div>
        </div>

        {/* Tabs */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Tabs</h3>
          <div className="border rounded-lg p-6 bg-background">
            <Tabs defaultValue="account" className="w-[400px]">
              <TabsList>
                <TabsTrigger value="account">Account</TabsTrigger>
                <TabsTrigger value="password">Password</TabsTrigger>
                <TabsTrigger value="settings">Settings</TabsTrigger>
              </TabsList>
              <TabsContent value="account" className="p-4">
                Account settings and preferences
              </TabsContent>
              <TabsContent value="password" className="p-4">
                Change your password here
              </TabsContent>
              <TabsContent value="settings" className="p-4">
                Other settings
              </TabsContent>
            </Tabs>
          </div>
        </div>

        {/* Tooltip */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Tooltip</h3>
          <div className="border rounded-lg p-6 bg-background">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="secondary" className="border">Hover me</Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>This is a tooltip</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
      </div>

      {/* Input Components */}
      <div className="space-y-8">
        <h2 className="text-2xl font-bold">Input Components</h2>
        
        {/* Textarea */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Textarea</h3>
          <div className="border rounded-lg p-6 bg-background">
            <div className="grid gap-4">
              <Textarea placeholder="Type your message here" />
              <Textarea placeholder="Disabled textarea" disabled />
            </div>
          </div>
        </div>
      </div>

      {/* Toast Demo */}
      <div className="space-y-8">
        <h2 className="text-2xl font-bold">Notifications</h2>
        
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Toast</h3>
          <div className="border rounded-lg p-6 bg-background">
            <ToastDemo />
          </div>
        </div>
      </div>

      {/* Toaster component */}
      <Toaster />

      {/* Calendar Demo */}
      <div className="space-y-8">
        <h2 className="text-2xl font-bold">Date & Time</h2>
        
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Calendar</h3>
          <div className="border rounded-lg p-6 bg-background">
            <div className="space-y-4">
              {/* Basic Calendar */}
              <div>
                <h4 className="text-md font-medium mb-2">Basic Calendar</h4>
                <Calendar
                  mode="single"
                  selected={new Date()}
                  className="rounded-md border"
                />
              </div>

              {/* Calendar with Popover */}
              <div>
                <h4 className="text-md font-medium mb-2">Calendar with Date Picker</h4>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="secondary"
                      className={cn(
                        "w-[280px] justify-start text-left font-normal",
                        !new Date() && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {new Date() ? format(new Date(), "PPP") : <span>Pick a date</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={new Date()}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Calendar with Range Selection */}
              <div>
                <h4 className="text-md font-medium mb-2">Calendar with Range Selection</h4>
                <Calendar
                  mode="range"
                  selected={{
                    from: new Date(),
                    to: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
                  }}
                  className="rounded-md border"
                />
              </div>

              {/* Calendar with Multiple Selection */}
              <div>
                <h4 className="text-md font-medium mb-2">Calendar with Multiple Selection</h4>
                <Calendar
                  mode="multiple"
                  selected={[new Date(), new Date(Date.now() + 2 * 24 * 60 * 60 * 1000)]}
                  className="rounded-md border"
                />
              </div>

              {/* Calendar with Custom Styling */}
              <div>
                <h4 className="text-md font-medium mb-2">Calendar with Custom Styling</h4>
                <Calendar
                  mode="single"
                  selected={new Date()}
                  className="rounded-md border"
                  classNames={{
                    months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
                    month: "space-y-4",
                    caption: "flex justify-center pt-1 relative items-center",
                    caption_label: "text-sm font-medium",
                    nav: "space-x-1 flex items-center",
                    nav_button: "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100",
                    nav_button_previous: "absolute left-1",
                    nav_button_next: "absolute right-1",
                    table: "w-full border-collapse space-y-1",
                    head_row: "flex",
                    head_cell: "text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]",
                    row: "flex w-full mt-2",
                    cell: "h-9 w-9 text-center text-sm p-0 relative [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
                    day: "h-9 w-9 p-0 font-normal aria-selected:opacity-100 hover:bg-accent hover:text-accent-foreground",
                    day_selected: "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
                    day_today: "bg-accent text-accent-foreground",
                    day_outside: "text-muted-foreground opacity-50",
                    day_disabled: "text-muted-foreground opacity-50",
                    day_range_middle: "aria-selected:bg-accent aria-selected:text-accent-foreground",
                    day_hidden: "invisible"
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Bar */}
      <Card className="p-6">
        <h2 className="text-xl font-bold mb-4">Navigation Bar</h2>
        <div className="space-y-4">
          <div className="border rounded-lg p-6 bg-background relative h-[200px]">
            {/* Mock content to show nav bar at bottom */}
            <div className="absolute bottom-0 left-0 right-0">
              <nav className="bg-neutral-100 h-20 relative">
                <div className="w-full h-20 flex justify-between">
                  {/* Home */}
                  <button className="w-20 pt-3 inline-flex flex-col items-center">
                    <div className="w-8 h-8 mb-2 flex items-center justify-center">
                      <HomeIcon 
                        size={20} 
                        className="text-New-OW-Dark group-hover:bg-[#e7fe52]" 
                      />
                    </div>
                    <div className="text-center text-New-OW-Dark text-sm font-lato font-bold leading-none tracking-wide">
                      Home
                    </div>
                  </button>

                  {/* My Cars */}
                  <button className="w-20 pt-3 inline-flex flex-col items-center">
                    <div className="w-8 h-8 mb-2 flex items-center justify-center">
                      <Car 
                        size={20}
                        className="text-New-OW-Dark group-hover:bg-[#e7fe52]"
                      />
                    </div>
                    <div className="text-center text-New-OW-Dark text-sm font-lato leading-none tracking-wide">
                      My Cars
                    </div>
                  </button>

                  {/* Smart Help (Center Button) */}
                  <div className="w-24 flex flex-col items-center h-full">
                    <button className="w-14 h-14 -mt-6 bg-[#e7fe52] rounded-[20px] shadow-[0px_10px_10px_0px_rgba(47,37,220,0.10),0px_4px_4px_0px_rgba(47,37,220,0.05),0px_1px_0px_0px_rgba(47,37,220,0.05)] outline outline-[3px] outline-offset-[-3px] outline-New-OW-Dark flex justify-center items-center">
                      <Plus size={20} className="text-New-OW-Dark" />
                    </button>
                    <div className="text-center text-New-OW-Dark text-sm font-lato leading-none tracking-wide mt-auto pt-3">
                      Smart Help
                    </div>
                  </div>

                  {/* History */}
                  <button className="w-20 pt-3 inline-flex flex-col items-center">
                    <div className="w-8 h-8 mb-2 flex items-center justify-center">
                      <HistoryIcon 
                        size={20}
                        className="text-New-OW-Dark group-hover:bg-[#e7fe52]"
                      />
                    </div>
                    <div className="text-center text-New-OW-Dark text-sm font-lato leading-none tracking-wide">
                      History
                    </div>
                  </button>

                  {/* Profile */}
                  <button className="w-20 pt-3 inline-flex flex-col items-center">
                    <div className="w-8 h-8 mb-2 flex items-center justify-center">
                      <UserIcon 
                        size={16}
                        className="text-New-OW-Dark group-hover:bg-[#e7fe52]"
                      />
                    </div>
                    <div className="text-center text-New-OW-Dark text-sm font-lato leading-none tracking-wide">
                      Profile
                    </div>
                  </button>
                </div>
              </nav>
            </div>
          </div>

          {/* States description */}
          <div className="space-y-2 text-sm text-muted-foreground">
            <p>• Active/clicked icons have green (#e7fe52) background fill</p>
            <p>• Active text uses bold weight, inactive uses normal weight</p>
            <p>• Center button uses green background with black outline</p>
            <p>• Navigation bar uses neutral-100 background</p>
            <p>• Uses Lato font family for all text</p>
          </div>
        </div>
      </Card>
    </div>
  );
}